import React, { ReactElement } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DocumentData } from 'firebase/firestore';
import { regularSm } from '../../../constants/location';
import { usageCountRule } from '../../../constants/enum';
import { formatTokenPrice, getOcrEngineDisplayName } from '../../../utils/ocrCostDisplay';

type OcrCostDisplayProps = {
  /** results/{id}.token_usage（または読取履歴の token_usage） */
  tokenUsage?: DocumentData;
  /**
   * 子パターン判定のコスト。旧名称（variant_classification）で記録された結果もあるため、
   * 呼び出し側で解決してから渡す。フリーOCRでは発生しないため未指定でよい。
   */
  subPatternClassificationCost?: DocumentData;
};

/**
 * 運営向けのOCRコスト表示（#3315）。
 *
 * パターンOCRの結果画面にしかなかったコスト表示を、フリーOCRでも同じ見せ方で
 * 出せるよう共通化した。自動仕分け・子パターン判定・構造化はフリーOCRでは
 * 発生しないが、いずれも個別に有無を判定しているため未設定なら描画されない。
 *
 * 運営スタッフ限定の情報のため、呼び出し側で必ず useDetailMode の isDetailView で
 * 出し分けること。
 */
const OcrCostDisplay = ({
  tokenUsage,
  subPatternClassificationCost,
}: OcrCostDisplayProps): ReactElement | null => {
  if (tokenUsage?.total_cost_jpy === undefined) return null;

  // 消費枚数の内訳（入力欄・文字数による加算枚数）
  const fieldUnitCount = Math.floor(
    (tokenUsage.output_field_count ?? 0) / usageCountRule.fieldsPerUnit,
  );
  const charUnitCount = Math.floor(
    (tokenUsage.total_char_count ?? 0) / usageCountRule.charsPerUnit,
  );
  const unitPrice = tokenUsage.price_per_unit_jpy ?? usageCountRule.defaultPricePerUnitJpy;
  const baseCount = 1 + fieldUnitCount + charUnitCount;

  // 粗利・消費枚数の計算に含まれないコスト（#3245）。
  // 仕分け・子パターン判定・マスタ照合リランクは、Functions 側で粗利を計算した後に
  // 合計（total_cost_jpy）へ足している。粗利の式は合計からこれらを除いた額で示さないと
  // 「N枚 × 単価 − 合計 = 粗利」が合わなくなる
  const nonBillableCostJpy =
    (tokenUsage.classification?.cost_jpy ?? 0) +
    (subPatternClassificationCost?.cost_jpy ?? 0) +
    (tokenUsage.master_rerank?.cost_jpy ?? 0);
  // 小数第2位で丸める（cost_jpy の桁に揃え、引き算の誤差を表示に出さない）
  const profitBaseCostJpy =
    Math.round(((tokenUsage.total_cost_jpy ?? 0) - nonBillableCostJpy) * 100) / 100;
  const hasClassificationCost = Boolean(tokenUsage.classification || subPatternClassificationCost);

  const baseProfit = baseCount * unitPrice - profitBaseCostJpy;
  const needsProfitAdjustment = baseProfit < usageCountRule.minGrossProfitJpy;

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1, fontSize: '12px', lineHeight: 1.6 }}>
          <Box
            sx={{
              mb: 1,
              borderBottom: '1px solid rgba(255,255,255,0.3)',
              pb: 0.5,
            }}
          >
            コスト内訳
          </Box>
          {/* 自動仕分け（#2967）。ホームからアップロードした場合のみ発生する。
              ユーザーへの課金は行わず、運営向けの原価表示にのみ使う */}
          {tokenUsage.classification && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#ce93d8' }}>自動仕分け</Box>
              <Box>モデル: {tokenUsage.classification.model_name}</Box>
              {/* 選択肢の件数（#3245）。記録前の結果には無いため、あるときだけ出す */}
              {tokenUsage.classification.candidate_count !== undefined && (
                <Box>選択肢: {tokenUsage.classification.candidate_count}件 + パターンなし</Box>
              )}
              <Box>
                入力: {tokenUsage.classification.input_tokens?.toLocaleString()} / 出力:{' '}
                {tokenUsage.classification.output_tokens?.toLocaleString()}
              </Box>
              <Box>コスト: {tokenUsage.classification.cost_jpy}円</Box>
            </Box>
          )}
          {/* 子パターン判定（#2967）。親が子パターンを持つときだけ発生する。
              ユーザーへの課金は行わず、運営向けの原価表示にのみ使う */}
          {subPatternClassificationCost && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#ce93d8' }}>子パターン判定</Box>
              <Box>モデル: {subPatternClassificationCost.model_name}</Box>
              {/* 選択肢の件数（#3245）。記録前の結果には無いため、あるときだけ出す */}
              {subPatternClassificationCost.candidate_count !== undefined && (
                <Box>選択肢: {subPatternClassificationCost.candidate_count}件 + 標準</Box>
              )}
              <Box>
                入力: {subPatternClassificationCost.input_tokens?.toLocaleString()} / 出力:{' '}
                {subPatternClassificationCost.output_tokens?.toLocaleString()}
              </Box>
              <Box>コスト: {subPatternClassificationCost.cost_jpy}円</Box>
            </Box>
          )}
          {/* 仕分けのコストは粗利・消費枚数に含めない参考値であることを示す（#3245） */}
          {hasClassificationCost && (
            <Box
              sx={{
                mb: 1,
                pb: 0.5,
                borderBottom: '1px dashed rgba(255,255,255,0.2)',
                fontSize: '10px',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              ※ 仕分けのコストは参考値です。粗利・消費枚数の計算には含みません
            </Box>
          )}
          {/* OCRエンジン（Vision AI または Document Intelligence） */}
          {tokenUsage.azure_vision && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#90caf9' }}>
                {getOcrEngineDisplayName(tokenUsage.azure_vision.engine)}
              </Box>
              <Box>トランザクション数: {tokenUsage.azure_vision.transaction_count}</Box>
              <Box>コスト: {tokenUsage.azure_vision.cost_jpy}円</Box>
            </Box>
          )}
          {/* Gemini OCR（値取得） */}
          {tokenUsage.gemini_ocr && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#a5d6a7' }}>Gemini 値取得</Box>
              <Box>モデル: {tokenUsage.gemini_ocr.model_name}</Box>
              <Box>
                入力: {tokenUsage.gemini_ocr.input_tokens?.toLocaleString()} tokens ×{' '}
                {formatTokenPrice(tokenUsage.gemini_ocr.input_price_per_million)}
                {tokenUsage.gemini_ocr.input_cost_jpy !== undefined && (
                  <span style={{ color: '#81c784', marginLeft: '4px' }}>
                    ({tokenUsage.gemini_ocr.input_cost_jpy}円)
                  </span>
                )}
              </Box>
              <Box>
                出力: {tokenUsage.gemini_ocr.output_tokens?.toLocaleString()} tokens ×{' '}
                {formatTokenPrice(tokenUsage.gemini_ocr.output_price_per_million)}
                {tokenUsage.gemini_ocr.output_cost_jpy !== undefined && (
                  <span style={{ color: '#81c784', marginLeft: '4px' }}>
                    ({tokenUsage.gemini_ocr.output_cost_jpy}円)
                  </span>
                )}
              </Box>
              <Box>
                思考: {(tokenUsage.gemini_ocr.thoughts_tokens ?? 0).toLocaleString()} tokens ×{' '}
                {formatTokenPrice(tokenUsage.gemini_ocr.thinking_price_per_million)}
                <span style={{ color: '#81c784', marginLeft: '4px' }}>
                  ({tokenUsage.gemini_ocr.thoughts_cost_jpy ?? 0}円)
                </span>
              </Box>
              <Box>
                コスト: {tokenUsage.gemini_ocr.input_cost_jpy ?? 0}円 +{' '}
                {tokenUsage.gemini_ocr.output_cost_jpy ?? 0}円 +{' '}
                {tokenUsage.gemini_ocr.thoughts_cost_jpy ?? 0}円 = {tokenUsage.gemini_ocr.cost_jpy}
                円
              </Box>
            </Box>
          )}
          {/* 構造化（Gemini or ChatGPT）。model_name で見出しを切り替える */}
          {tokenUsage.gemini_structured && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#ffcc80' }}>
                {String(tokenUsage.gemini_structured.model_name ?? '').includes('gpt-4o')
                  ? 'ChatGPT 構造化'
                  : 'Gemini 構造化'}
              </Box>
              <Box>モデル: {tokenUsage.gemini_structured.model_name}</Box>
              <Box>
                入力: {tokenUsage.gemini_structured.input_tokens?.toLocaleString()} tokens ×{' '}
                {formatTokenPrice(tokenUsage.gemini_structured.input_price_per_million)}
                {tokenUsage.gemini_structured.input_cost_jpy !== undefined && (
                  <span style={{ color: '#ffb74d', marginLeft: '4px' }}>
                    ({tokenUsage.gemini_structured.input_cost_jpy}円)
                  </span>
                )}
              </Box>
              <Box>
                出力: {tokenUsage.gemini_structured.output_tokens?.toLocaleString()} tokens ×{' '}
                {formatTokenPrice(tokenUsage.gemini_structured.output_price_per_million)}
                {tokenUsage.gemini_structured.output_cost_jpy !== undefined && (
                  <span style={{ color: '#ffb74d', marginLeft: '4px' }}>
                    ({tokenUsage.gemini_structured.output_cost_jpy}円)
                  </span>
                )}
              </Box>
              <Box>
                思考: {(tokenUsage.gemini_structured.thoughts_tokens ?? 0).toLocaleString()} tokens
                × {formatTokenPrice(tokenUsage.gemini_structured.thinking_price_per_million)}
                <span style={{ color: '#ffb74d', marginLeft: '4px' }}>
                  ({tokenUsage.gemini_structured.thoughts_cost_jpy ?? 0}円)
                </span>
              </Box>
              <Box>
                コスト: {tokenUsage.gemini_structured.input_cost_jpy ?? 0}円 +{' '}
                {tokenUsage.gemini_structured.output_cost_jpy ?? 0}円 +{' '}
                {tokenUsage.gemini_structured.thoughts_cost_jpy ?? 0}円 ={' '}
                {tokenUsage.gemini_structured.cost_jpy}円
              </Box>
            </Box>
          )}
          {/* マスタ照合のAIリランク（#3355）。マッピングで有効にした場合のみ発生する。
              ユーザーへの課金は行わず、運営向けの原価表示にのみ使う。
              明細の行数・マッピング数だけ呼ばれるため、トークン数は何回分の合計か
              読み取れない。回数と金額だけを出す */}
          {tokenUsage.master_rerank && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#ce93d8' }}>マスタ照合AI選択</Box>
              <Box>モデル: {tokenUsage.master_rerank.model_name}</Box>
              <Box>実行回数: {tokenUsage.master_rerank.call_count ?? '-'}回</Box>
              <Box>コスト: {tokenUsage.master_rerank.cost_jpy}円</Box>
            </Box>
          )}
          {/* 実行情報 */}
          {tokenUsage.page_count !== undefined && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontWeight: 'bold', color: '#ce93d8' }}>実行情報</Box>
              <Box>ページ数: {tokenUsage.page_count}</Box>
              <Box>項目数: {tokenUsage.pattern_item_count}</Box>
              <Box>Gemini実行回数: {tokenUsage.gemini_execution_count}</Box>
              <Box>合計トークン: {tokenUsage.total_gemini_tokens?.toLocaleString()}</Box>
              <Box>1ページあたり: {tokenUsage.tokens_per_page?.toLocaleString()} tokens</Box>
            </Box>
          )}
          {/* 合計 */}
          <Box
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.3)',
              pt: 0.5,
              mt: 1,
              fontWeight: 'bold',
            }}
          >
            合計: {tokenUsage.total_cost_jpy}円
            <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
              (${tokenUsage.total_cost_usd?.toFixed(6)} USD × {tokenUsage.exchange_rate}円)
            </Box>
            {nonBillableCostJpy > 0 && (
              <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontWeight: 'normal' }}>
                うち粗利計算の対象外（仕分け・リランク）:{' '}
                {Math.round(nonBillableCostJpy * 100) / 100}円
              </Box>
            )}
          </Box>
          {/* 粗利 */}
          {tokenUsage.gross_profit_jpy !== undefined && (
            <Box
              sx={{
                borderTop: '1px solid rgba(255,255,255,0.3)',
                pt: 0.5,
                mt: 1,
              }}
            >
              <Box sx={{ fontWeight: 'bold', color: '#a5d6a7' }}>粗利</Box>
              <Box>
                {tokenUsage.calculated_usage_count}枚 × {unitPrice}円 - {profitBaseCostJpy}円 ={' '}
                <span
                  style={{
                    fontWeight: 'bold',
                    color:
                      tokenUsage.gross_profit_jpy >= usageCountRule.minGrossProfitJpy
                        ? '#a5d6a7'
                        : '#ef9a9a',
                  }}
                >
                  {tokenUsage.gross_profit_jpy}円
                </span>
              </Box>
              <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                消費枚数 = 基本1枚 + 入力欄({fieldUnitCount}枚) + 文字数({charUnitCount}枚)
                {needsProfitAdjustment ? ' + 粗利補正(1枚)' : ''}
              </Box>
              <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
                入力欄数: {tokenUsage.output_field_count ?? '-'}個 （{usageCountRule.fieldsPerUnit}
                個ごとに+1枚 → {fieldUnitCount}枚）
              </Box>
              <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
                文字数: {tokenUsage.total_char_count?.toLocaleString() ?? '-'}文字（
                {usageCountRule.charsPerUnit.toLocaleString()}文字ごとに+1枚 → {charUnitCount}枚）
              </Box>
              {needsProfitAdjustment && (
                <Box sx={{ fontSize: '10px', color: '#ef9a9a' }}>
                  粗利補正: {baseCount}枚 × {unitPrice}円 - {profitBaseCostJpy}円 ={' '}
                  {baseProfit.toFixed(2)}円 &lt; {usageCountRule.minGrossProfitJpy}円 → +1枚
                </Box>
              )}
            </Box>
          )}
        </Box>
      }
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(50, 50, 50, 0.95)',
            maxWidth: 320,
            '& .MuiTooltip-arrow': {
              color: 'rgba(50, 50, 50, 0.95)',
            },
          },
        },
      }}
    >
      <Typography
        sx={{ ...regularSm, cursor: 'pointer', textDecoration: 'underline dotted' }}
        textAlign="center"
      >
        コスト：{tokenUsage.total_cost_jpy}円
      </Typography>
    </Tooltip>
  );
};

export default OcrCostDisplay;
